import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { allValidations } from "../../utils/formValidations";
import { showFormErrors } from "../../utils/commonFunctions";
import {
    addService,
    createServiceItem,
    deleteAddtionalService,
    deleteServiceItem,
    getService,
    updateService,
    updateServiceItem,
} from "../../redux/actions/serviceAction";
import { HOST } from "../../constants";
import { confirmPopup } from "primereact/confirmpopup";
import Index from "./index";

export default function ServiceContainer() {
    const { allServices } = Index();
    const dispatch = useDispatch();
    const history = useHistory();

    const { id } = useParams();
    let { serviceDetails } = useSelector((state) => state.service);

    const [data, setData] = useState({
        service: null,
        serviceType: "",
        serviceName: "",
        description: "",
        serviceDescription: "",
        iconImage: "",
        about: {
            title: "",
            description: "",
        },
        hasBeforeAfterImages: true,
        primaryBeforeImage: "",
        primaryAfterImage: "",
        secondaryImageTitle: "",
        secondaryBeforeImage: "",
        secondaryAfterImage: "",
        primarySingleImage: "",
        secondarySingleImage: "",
        features: [
            {
                description: "",
            },
        ],
        benefitsTitle: "",
        benefits: [
            {
                iconImage: "",
                title: "",
                description: "",
            },
        ],
        items: [],
        price: 0,
        additionalServices: [],
    });

    const defaultIgnoreField = ["service", "items", "additionalServices"];

    const [ignoreFields, setIgnoreFields] = useState([...defaultIgnoreField, "primarySingleImage", "secondarySingleImage"]);
    const serviceTypes = [
        { name: "Virtual Staging", value: "VIRTUALSTAGING" },
        { name: "360 Virtual Tour", value: "360VIRTUALTOUR" },
        { name: "Virtual Renovation", value: "VIRTUALRENOVATION" },
        { name: "2D Floor", value: "2DFLOOR" },
        { name: "3D Renderers", value: "3DRENDERS" },
    ];

    useEffect(() => {
        if (id) {
            dispatch(getService(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, dispatch]);

    useEffect(() => {
        if (id) {
            if (serviceDetails && !serviceDetails?.hasBeforeAfterImages)
                setIgnoreFields([...defaultIgnoreField, "primaryBeforeImage", "primaryAfterImage", "secondaryBeforeImage", "secondaryAfterImage"]);
            setData({
                ...serviceDetails,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceDetails]);

    const [formType, setFormType] = useState("EDIT");
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [additionalItemType, setAdditionalItemType] = useState(false);

    const [item, setItem] = useState({
        _id: null,
        service: id,
        name: "",
        price: "",
    });

    const resetItemForm = () => {
        setItem({
            _id: null,
            service: id,
            name: "",
            price: "",
        });
    };

    const addFeature = () => {
        setData((prev) => ({ ...prev, features: [...(prev.features || []), { description: "" }] }));
    };

    const removeFeature = (index) => {
        setData((prev) => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
    };

    const addBenefit = () => {
        setData((prev) => ({
            ...prev,
            benefits: [
                ...(prev.benefits || [
                    {
                        iconImage: "",
                        title: "",
                        description: "",
                    },
                ]),
                {
                    iconImage: "",
                    title: "",
                    description: "",
                },
            ],
        }));
    };

    const removeBenefit = (index) => {
        setData((prev) => ({ ...prev, benefits: prev.benefits.filter((_, i) => i !== index) }));
    };

    const handleChange = ({ name, value, formArrayName, index }) => {
        if (formType !== "VIEW") {
            if (name === "hasBeforeAfterImages" && value) {
                setIgnoreFields([...defaultIgnoreField, "primarySingleImage", "secondarySingleImage"]);
            } else if (name === "hasBeforeAfterImages" && !value) {
                setIgnoreFields([...defaultIgnoreField, "primaryBeforeImage", "primaryAfterImage", "secondaryBeforeImage", "secondaryAfterImage"]);
            }

            let formErrors = {};
            if (formArrayName) {
                const _newData = { ...data };
                let obj = _newData[formArrayName][index];
                obj[name] = value;
                const formErrors = allValidations(name, value, obj, ignoreFields);
                obj.formErrors = formErrors;
                _newData[formArrayName][index] = obj;
                setData(() => ({
                    ..._newData,
                }));
            } else if (name.includes(".")) {
                // Only works for single object, e.g: formErrors: { about: { title: '', description: '' } }
                // Todo: Make it work for nested objects
                const splitNames = name.split(".");
                const error = allValidations(splitNames[splitNames.length - 1], value, data, ignoreFields);
                setData((prev) => ({
                    ...prev,
                    [splitNames[0]]: { ...prev[splitNames[0]], [splitNames[1]]: value },
                    formErrors: {
                        ...prev.formErrors,
                        [splitNames[0]]: { ...prev.formErrors?.[splitNames[0]], [splitNames[1]]: error[splitNames[1]] },
                    },
                }));
            } else {
                formErrors = allValidations(name, value, data, ignoreFields);
                setData((prev) => ({ ...prev, [name]: value, formErrors }));
            }
        }
    };

    const onPreview = (type) => {
        const imageField = [
            "iconImage",
            "primarySingleImage",
            "secondarySingleImage",
            "primaryBeforeImage",
            "primaryAfterImage",
            "secondaryBeforeImage",
            "secondaryAfterImage",
        ];
        // Create a copy of data to avoid directly mutating the state
        const updatedData = { ...data };

        // Update image fields to use value[0]
        imageField.forEach((field) => {
            if (Array.isArray(updatedData[field]) && updatedData[field].length > 0) {
                updatedData[field] = updatedData[field][0];
            }
        });

        // Perform validation and navigation
        if (showFormErrors(updatedData, setData, ignoreFields)) {
            history.push({ pathname: `/services/preview`, state: { ...updatedData, formType: type } });
        }
    };

    const onSubmit = () => {
        if (data._id) {
            dispatch(updateService(id ?? data._id, data, history));
        } else {
            dispatch(addService(data, setLoading, history));
        }
    };

    const addItem = (additionalItemType) => {
        setVisible(true);
        setAdditionalItemType(additionalItemType);
    };

    const editItem = (additionalItemType, item) => {
        setItem({ ...item });
        setVisible(true);
        setAdditionalItemType(additionalItemType);
    };

    const handleItemChange = ({ name, value }) => {
        if (formType !== "VIEW") {
            const formErrors = allValidations(name, value, item);
            setItem((prev) => ({ ...prev, [name]: value, formErrors }));
        }
    };

    const createItem = () => {
        if (item?._id) {
            dispatch(updateServiceItem(item._id, item, additionalItemType, setVisible, resetItemForm));
        } else {
            dispatch(createServiceItem(item, additionalItemType, setVisible, resetItemForm));
        }
    };

    const onDeleteItem = (e, itemId) => {
        confirmPopup({
            target: e.currentTarget,
            message:
                "Are you sure you want to delete this service item? This action will permanently delete the user account. This action cannot be undone.",
            icon: "pi pi-exclamation-triangle",
            accept: () => dispatch(deleteServiceItem(itemId, id)),
            reject: () => { },
        });
    };

    const onDeleteAdditionalService = (e, additionalServiceId) => {
        confirmPopup({
            target: e.currentTarget,
            message:
                "Are you sure you want to delete this additonal service? This action will permanently delete the user account. This action cannot be undone.",
            icon: "pi pi-exclamation-triangle",
            accept: () => dispatch(deleteAddtionalService(additionalServiceId, id)),
            reject: () => { },
        });
    };

    return {
        dispatch,
        id,
        data,
        handleChange,
        loading,
        serviceTypes,
        onSubmit,
        onPreview,
        setFormType,
        addFeature,
        removeFeature,
        addBenefit,
        removeBenefit,
        history,
        HOST,
        setData,
        visible,
        setVisible,
        item,
        addItem,
        editItem,
        handleItemChange,
        createItem,
        onDeleteItem,
        resetItemForm,
        onDeleteAdditionalService,
        services: allServices,
        setLoading,
    };
}
